import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { UsersApi } from '@core/api/users/users.api';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { debounceTime, skip, switchMap } from 'rxjs/operators';
import english from '../../../../main/maintenances/i18n/en.json';
import spanish from '../../../../main/maintenances/i18n/es.json';
import portuguese from '../../../../main/maintenances/i18n/pt.json';
import { User } from './../../../interfaces';

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RequestersComponent),
  multi: true
};

@Component({
  selector: 'requesters',
  templateUrl: './requesters.component.html',
  styleUrls: ['./requesters.component.scss'],
  providers: [INPUT_FIELD_VALUE_ACCESSOR]
})
export class RequestersComponent implements ControlValueAccessor, OnInit {
  @Input() required = false;
  @Input() disabled = false;
  @Input() floatLabel = true;
  @Input() appearance = 'outline';

  requesters: User[] = [];
  inputValue: FormControl = new FormControl();
  selectedRequester: User;

  loading = false;

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private usersApi: UsersApi
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  ngOnInit(): void {
    if (this.disabled) {
      this.inputValue.disable();
    }

    if (this.required) {
      this.inputValue.setValidators(Validators.required);
    }

    this.inputValue.valueChanges.pipe(
      skip(1),
      debounceTime(500),
      switchMap((searchTerm) => {
        this.loading = true;
        this.requesters = [];

        return this.usersApi.search({ name_contains: searchTerm, archived: false }, { limit: 1000, orderBy: 'name_asc' });
      })
    ).subscribe((response) => {
      this.loading = false;
      this.requesters = response.items;
    });
  }

  onChange: (_: any) => void = () => {};
  onTouched: (_: any) => void = () => {};

  writeValue(requesterId: string): void {
    if (requesterId) {
      this.usersApi.searchOne(requesterId).subscribe((requester) => {
        this.selectedRequester = requester;
        this.inputValue.setErrors(null);
      });

      return;
    }

    this.deselectRequester();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setSelectedRequester(): void {
    if (this.inputValue.value) {
      this.selectedRequester = this.requesters.find((requester) => (
        requester.id === this.inputValue.value
      ));

      this.onChange(this.inputValue.value);
      this.inputValue.setErrors(null);
    }
  }

  deselectRequester(): void {
    this.onChange(null);
    this.inputValue.setValue(null);
    this.inputValue.setErrors(null);
    this.selectedRequester = null;
  }
}
